import React, { useState } from 'react';
import { X, Activity, Heart, Brain, Battery, Bell, Clock, Moon } from 'lucide-react';

interface SleepCoachProps {
  onClose: () => void;
  currentSleepScore: number;
}

interface EnergyMode {
  id: 'peak' | 'perform' | 'getby';
  name: string;
  description: string;
  minHours: number;
  icon: React.ReactNode;
}

const energyModes: EnergyMode[] = [
  {
    id: 'peak',
    name: 'Peak',
    description: 'Maximum energy and performance',
    minHours: 8,
    icon: <Battery className="w-4 h-4" />
  },
  {
    id: 'perform',
    name: 'Perform',
    description: 'Balanced energy levels',
    minHours: 6,
    icon: <Brain className="w-4 h-4" />
  },
  {
    id: 'getby',
    name: 'Get By',
    description: 'Minimum required rest',
    minHours: 4,
    icon: <Clock className="w-4 h-4" />
  }
];

export function SleepCoach({ onClose, currentSleepScore }: SleepCoachProps) {
  const [selectedMode, setSelectedMode] = useState<EnergyMode['id']>('peak');
  const [wakeTime, setWakeTime] = useState('07:00');
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);

  // Calculate sleep debt
  const sleepDebt = Math.max(0, 8 - 6.5);
  const sleepDebtImpact = sleepDebt > 2 ? 'high' : sleepDebt > 1 ? 'medium' : 'low';

  // Calculate bedtime and wind down time based on wake time and selected mode
  const calculateTimes = () => {
    const [wakeHours, wakeMinutes] = wakeTime.split(':').map(Number);
    const mode = energyModes.find(m => m.id === selectedMode);
    const requiredSleep = (mode?.minHours || 8) + (sleepDebt > 0 ? 1 : 0);

    // Calculate bedtime
    let bedtimeHours = wakeHours - requiredSleep;
    if (bedtimeHours < 0) bedtimeHours += 24;
    const bedtime = `${bedtimeHours.toString().padStart(2, '0')}:${wakeMinutes.toString().padStart(2, '0')}`;

    // Calculate wind down time (30 minutes before bedtime)
    let windDownHours = bedtimeHours;
    let windDownMinutes = wakeMinutes - 30;
    if (windDownMinutes < 0) {
      windDownMinutes += 60;
      windDownHours = windDownHours === 0 ? 23 : windDownHours - 1;
    }
    const windDownTime = `${windDownHours.toString().padStart(2, '0')}:${windDownMinutes.toString().padStart(2, '0')}`;

    return { bedtime, windDownTime };
  };

  const { bedtime, windDownTime } = calculateTimes();

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[9999] overflow-y-auto">
      <div className="container mx-auto px-4 py-6">
        <div className="max-w-2xl mx-auto space-y-6">
          {/* Header */}
          <div className="theme-card p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Moon className="w-6 h-6 text-primary" />
                  <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full" />
                </div>
                <h2 className="text-xl font-semibold">Plan My Sleep</h2>
              </div>
              <button
                onClick={onClose}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Sleep Debt Info */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">Current Sleep Score</div>
                <div className="text-2xl font-bold">{currentSleepScore}%</div>
              </div>
              {sleepDebt > 0 && (
                <div className={`bg-${sleepDebtImpact === 'high' ? 'destructive' : 'warning'}/10 text-${sleepDebtImpact === 'high' ? 'destructive' : 'warning'} rounded-lg p-4 text-sm`}>
                  {`You have accumulated ${sleepDebt} hours of sleep debt. Consider selecting Peak mode (${energyModes[0].minHours}+ hours) to help clear your sleep debt.`}
                </div>
              )}
            </div>
          </div>

          {/* Energy Mode Selection */}
          <div className="theme-card p-6">
            <h3 className="text-lg font-semibold mb-4">Select Energy Mode</h3>
            <div className="grid grid-cols-3 gap-4">
              {energyModes.map((mode) => (
                <button
                  key={mode.id}
                  onClick={() => setSelectedMode(mode.id)}
                  className={`p-4 rounded-lg text-left relative group overflow-hidden ${
                    selectedMode === mode.id
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted hover:bg-muted/70'
                  }`}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="relative">
                    <div className="flex items-center gap-2 mb-2">
                      {mode.icon}
                      <span className="font-medium">{mode.name}</span>
                    </div>
                    <p className="text-xs opacity-90">{mode.description}</p>
                    <div className="mt-2 text-xs font-medium">
                      {mode.minHours}+ hours
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Time Selection */}
          <div className="theme-card p-6">
            <h3 className="text-lg font-semibold mb-4">Set Wake Time</h3>
            <div className="flex justify-center mb-6">
              <input
                type="time"
                value={wakeTime}
                onChange={(e) => setWakeTime(e.target.value)}
                className="theme-input px-4 py-2 text-lg font-medium"
              />
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Recommended Bedtime</span>
                <span className="font-medium">{bedtime}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Wind Down Reminder</span>
                <span className="font-medium">{windDownTime}</span>
              </div>
            </div>
          </div>

          {/* Notifications */}
          <div className="theme-card p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Bell className="w-5 h-5 text-primary" />
                <div>
                  <div className="font-medium">Bedtime Reminders</div>
                  <div className="text-sm text-muted-foreground">Get notified when it's time to wind down</div>
                </div>
              </div>
              <button
                onClick={() => setNotificationsEnabled(!notificationsEnabled)}
                className={`w-11 h-6 rounded-full transition-colors relative ${
                  notificationsEnabled ? 'bg-primary' : 'bg-muted'
                }`}
              >
                <div
                  className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                    notificationsEnabled ? 'right-1' : 'left-1'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Save Button */}
          <button
            onClick={onClose}
            className="theme-button w-full py-3 rounded-lg font-medium"
          >
            Save Sleep Schedule
          </button>
        </div>
      </div>
    </div>
  );
}